import { Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePayloadSchema } from "@/hooks/usePayloads";
import { SchemaFormRenderer } from "./SchemaFormRenderer";
import type { PayloadSummary } from "@/types/api";

interface PayloadConfigPanelProps {
  payload: PayloadSummary;
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
}

export function PayloadConfigPanel({ payload, config, onConfigChange }: PayloadConfigPanelProps) {
  const { data: schemaResponse, isLoading, error, refetch } = usePayloadSchema(
    payload.kind || "",
    payload.identifier || ""
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Loading schema for {payload.title || payload.identifier}…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
        <AlertCircle className="h-6 w-6 text-destructive" />
        <p className="text-sm text-destructive">Failed to load schema</p>
        <p className="text-xs text-muted-foreground max-w-sm">
          {error instanceof Error ? error.message : "An unexpected error occurred"}
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-1" /> Retry
        </Button>
      </div>
    );
  }

  if (!schemaResponse?.jsonSchema) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">
        No schema available for this payload. Configuration will use defaults.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border-b pb-3">
        <h3 className="font-medium">{schemaResponse.title || payload.title || payload.identifier}</h3>
        {schemaResponse.description && (
          <p className="text-sm text-muted-foreground mt-1">{schemaResponse.description}</p>
        )}
      </div>
      <SchemaFormRenderer
        schema={schemaResponse.jsonSchema as Record<string, any>}
        values={config as Record<string, any>}
        onChange={onConfigChange}
      />
    </div>
  );
}
